import { useCallback, useEffect, useRef, useState } from "react";
import type { DocumentSnapshot } from "firebase/firestore";
import { getOrganizationsPage } from "../../../api/organizations";
import type { OrganizationRecord } from "../../../api/organizations";
import { PAGE_SIZE } from "../constants";

export function useOrganizationsData() {
  const [records, setRecords] = useState<OrganizationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageCursorsRef = useRef<(DocumentSnapshot | null)[]>([null]);
  const [hasMore, setHasMore] = useState(false);

  const loadPage = useCallback(async (page: number) => {
    setLoading(true);
    setError(null);
    try {
      const cursor = pageCursorsRef.current[page - 1] ?? null;
      const {
        organizations,
        lastDoc,
        hasMore: nextHasMore,
      } = await getOrganizationsPage(cursor, PAGE_SIZE);
      setRecords(organizations);
      setHasMore(nextHasMore);
      const next = [...pageCursorsRef.current];
      while (next.length <= page) next.push(null);
      next[page] = lastDoc;
      pageCursorsRef.current = next;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load organizations",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPage(currentPage);
  }, [currentPage, loadPage]);

  const goToPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  return {
    records,
    setRecords,
    loading,
    error,
    setError,
    currentPage,
    hasMore,
    goToPage,
    loadPage,
  };
}
